import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Space,
  Tag,
  DatePicker,
  Select,
  Input,
  Button,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  message
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OperationLogManagement = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ modules: [], actions: [], targetTypes: [] });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    module: undefined,
    action: undefined,
    username: '',
    targetType: undefined,
    status: undefined,
    dateRange: []
  });
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [pagination.current, pagination.pageSize]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.current);
      params.append('pageSize', pagination.pageSize);

      if (filters.module) params.append('module', filters.module);
      if (filters.action) params.append('action', filters.action);
      if (filters.username) params.append('username', filters.username);
      if (filters.targetType) params.append('targetType', filters.targetType);
      if (filters.status) params.append('status', filters.status);
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.append('startDate', filters.dateRange[0].format('YYYY-MM-DD'));
        params.append('endDate', filters.dateRange[1].format('YYYY-MM-DD'));
      }

      const response = await axios.get(`${API_BASE}/operation-logs?${params.toString()}`, {
        headers: AuthService.getAuthHeaders()
      });

      if (response.data.success) {
        setLogs(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
      }
    } catch (error) {
      message.error('获取操作日志失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/operation-logs/stats`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/operation-logs/filter-options`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({
      module: undefined,
      action: undefined,
      username: '',
      targetType: undefined,
      status: undefined,
      dateRange: []
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    setTimeout(fetchLogs, 0);
  };

  const handleViewDetail = (record) => {
    setSelectedLog(record);
    setDetailVisible(true);
  };

  const getModuleLabel = (module) => {
    const labels = {
      tour: '出团管理',
      tourist: '游客管理',
      salesperson: '员工管理',
      destination: '产品管理',
      hotel: '酒店管理',
      flightBatch: '航班批次',
      addOnProduct: 'Add-ons产品',
      tourNotice: '出团通知',
      attachment: '附件管理',
      system: '系统设置'
    };
    return labels[module] || module;
  };

  const getActionLabel = (action) => {
    const labels = {
      create: '新增',
      update: '修改',
      delete: '删除',
      import: '导入',
      export: '导出',
      upload: '上传',
      publish: '发布',
      unpublish: '取消发布',
      assign: '分配',
      batch: '批量操作',
      view: '查看'
    };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'green',
      update: 'blue',
      delete: 'red',
      import: 'purple',
      export: 'cyan',
      upload: 'orange',
      publish: 'green',
      unpublish: 'default',
      assign: 'geekblue',
      batch: 'magenta'
    };
    return colors[action] || 'default';
  };

  const columns = [
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 155,
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作人',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      render: (text, record) => (
        <span>
          {text || record.username}
          {record.role && <Tag size="small" style={{ marginLeft: 4 }}>{record.role}</Tag>}
        </span>
      )
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
      width: 120,
      render: (text) => getModuleLabel(text)
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (text) => <Tag color={getActionColor(text)}>{getActionLabel(text)}</Tag>
    },
    {
      title: '操作对象',
      key: 'target',
      width: 150,
      ellipsis: true,
      render: (_, record) => (
        <span>
          {record.targetType && <Tag size="small">{record.targetType}</Tag>}
          {record.targetName || (record.targetId ? `ID: ${record.targetId}` : '-')}
        </span>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      render: (text) => text || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        status === 'success'
          ? <Tag icon={<CheckCircleOutlined />} color="success">成功</Tag>
          : <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日操作"
              value={stats?.todayCount || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="今日失败"
              value={stats?.todayFailedCount || 0}
              valueStyle={{ color: stats?.todayFailedCount > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总操作数"
              value={stats?.totalCount || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="操作模块数"
              value={stats?.moduleStats?.length || 0}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={4}>
            <Select
              placeholder="选择模块"
              allowClear
              style={{ width: '100%' }}
              value={filters.module}
              onChange={(value) => setFilters(prev => ({ ...prev, module: value }))}
            >
              {filterOptions.modules.map(m => (
                <Option key={m} value={m}>{getModuleLabel(m)}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="选择操作"
              allowClear
              style={{ width: '100%' }}
              value={filters.action}
              onChange={(value) => setFilters(prev => ({ ...prev, action: value }))}
            >
              {filterOptions.actions.map(a => (
                <Option key={a} value={a}>{getActionLabel(a)}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Input
              placeholder="操作人"
              value={filters.username}
              onChange={(e) => setFilters(prev => ({ ...prev, username: e.target.value }))}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="状态"
              allowClear
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="success">成功</Option>
              <Option value="failed">失败</Option>
            </Select>
          </Col>
          <Col span={5}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates || [] }))}
            />
          </Col>
          <Col span={3}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 数据表格 */}
      <Card title="操作日志列表">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
            }
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="操作日志详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {selectedLog && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="操作时间" span={2}>
              {dayjs(selectedLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="操作人">
              {selectedLog.name || selectedLog.username}
            </Descriptions.Item>
            <Descriptions.Item label="角色">
              {selectedLog.role || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="模块">
              {getModuleLabel(selectedLog.module)}
            </Descriptions.Item>
            <Descriptions.Item label="操作">
              <Tag color={getActionColor(selectedLog.action)}>
                {getActionLabel(selectedLog.action)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="操作对象类型">
              {selectedLog.targetType || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="操作对象ID">
              {selectedLog.targetId || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="操作对象名称" span={2}>
              {selectedLog.targetName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="操作描述" span={2}>
              {selectedLog.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="IP地址">
              {selectedLog.ipAddress || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {selectedLog.status === 'success'
                ? <Tag color="success">成功</Tag>
                : <Tag color="error">失败</Tag>
              }
            </Descriptions.Item>
            <Descriptions.Item label="浏览器" span={2}>
              <span style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {selectedLog.userAgent || '-'}
              </span>
            </Descriptions.Item>
            {selectedLog.errorMessage && (
              <Descriptions.Item label="错误信息" span={2}>
                <span style={{ color: '#ff4d4f' }}>{selectedLog.errorMessage}</span>
              </Descriptions.Item>
            )}
            {selectedLog.details && (
              <Descriptions.Item label="操作详情" span={2}>
                <pre style={{
                  background: '#f5f5f5',
                  padding: 8,
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto',
                  fontSize: 12
                }}>
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default OperationLogManagement;
